import React, { useMemo } from 'react';
import { Search } from 'lucide-react';
import { SearchResultGroup } from './SearchResultGroup';
import { SearchResultItem } from './SearchResultItem';
import { estimateKeywordPage } from './searchUtils';

interface ChunkSearchResult {
  documentId: string;
  pageNumber: number | null;
  endPageNumber: number | null;
  content: string;
  chunkIndex: number;
  score: number;
  hasKeywordMatch: boolean;
}

interface EnhancedSearchResultsProps {
  query: string;
  results: ChunkSearchResult[];
  documents: Array<{ documentId: string; filename: string }>;
  onOpenDocument: (request: { documentId: string; documentName: string; pageNumber?: number; searchText?: string }) => void;
}

export const EnhancedSearchResults: React.FC<EnhancedSearchResultsProps> = ({
  query,
  results,
  documents,
  onOpenDocument,
}) => {
  const groupedResults = useMemo(() => {
    const groups = new Map<string, { documentName: string; chunks: ChunkSearchResult[] }>();
    for (const chunk of results) {
      if (!groups.has(chunk.documentId)) {
        const doc = documents.find((d) => d.documentId === chunk.documentId);
        groups.set(chunk.documentId, {
          documentName: doc?.filename ?? 'Unknown document',
          chunks: [],
        });
      }
      groups.get(chunk.documentId)!.chunks.push(chunk);
    }
    return Array.from(groups.values());
  }, [results, documents]);

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 px-4 text-center">
        <Search className="h-6 w-6 text-medical-300 dark:text-medical-600 mb-2" />
        <p className="text-xs text-medical-500 dark:text-medical-400">
          No matching records found for &ldquo;{query}&rdquo;
        </p>
        <p className="text-[10px] text-medical-400 dark:text-medical-500 mt-1 leading-relaxed">
          Try different keywords or use broader natural language phrases.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
      {/* Summary line */}
      <p className="text-[10px] text-medical-400 px-1">
        {results.length} result{results.length !== 1 ? 's' : ''} across{' '}
        {groupedResults.length} document{groupedResults.length !== 1 ? 's' : ''}
      </p>

      {groupedResults.map(({ documentName, chunks }) => {
        const documentId = chunks[0].documentId;
        return (
          <SearchResultGroup
            key={documentId}
            documentName={documentName}
            matchCount={chunks.length}
            onClickHeader={() =>
              onOpenDocument({ documentId, documentName })
            }
          >
            {chunks.map((chunk) => (
              <SearchResultItem
                key={`${chunk.documentId}-${chunk.chunkIndex}`}
                chunk={chunk}
                query={query}
                onClick={() =>
                  onOpenDocument({
                    documentId: chunk.documentId,
                    documentName,
                    pageNumber: chunk.hasKeywordMatch
                      ? estimateKeywordPage(chunk.content, query, chunk.pageNumber, chunk.endPageNumber)
                      : chunk.pageNumber ?? undefined,
                    searchText: chunk.hasKeywordMatch ? query.trim() : undefined,
                  })
                }
              />
            ))}
          </SearchResultGroup>
        );
      })}
    </div>
  );
};
